/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unstable-nested-components */
import * as Slider from "@radix-ui/react-slider";
import { useFinancialsAvailability } from "components/financials/modern/data/hooks";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import lightFormat from "date-fns/lightFormat";
import max from "date-fns/max";
import min from "date-fns/min";
import { invariant } from "outvariant";
import { FormattedMessage } from "react-intl";
import { Value } from "react-powerplug";
import type { IIndicatorDataSource, IPlatformVertical, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface ReportDatesRangeProps {
  loading: boolean;
  tickerSymbol: string;
  country: ISecurityCountry;
  vertical: IPlatformVertical;
  statementType: IFinancialsStatementType;
  reportDates: Date[];
  indicatorDataSource: IIndicatorDataSource;
  interim?: string | null;
  last?: number;
  limit?: number;
  disabled?: boolean;
  value?: [Date | null, Date | null];
  onChange?: (value: [Date, Date]) => void;
}

export function getSafeFilterByReportDates(value?: [Date | null, Date | null]): [Date, Date] | null {
  if (value && value[0] && value[1]) {
    return [value[0], value[1]];
  }
  return null;
}

function adjustedLast(last: number, statementType: IFinancialsStatementType) {
  if (statementType === IFinancialsStatementType.Monthly) {
    return last / 12;
  }
  if (statementType === IFinancialsStatementType.Quarterly) {
    return last / 4;
  }
  return last;
}
function adjustedMovingRangeInYears(
  totalCount: number,
  statementType: IFinancialsStatementType,
  interim: string | null,
) {
  if (statementType === IFinancialsStatementType.Monthly) {
    return totalCount * 12;
  }
  if (statementType === IFinancialsStatementType.Quarterly && !interim) {
    return totalCount * 4;
  }
  return totalCount;
}

export default function ReportDatesRange({
  loading,
  tickerSymbol,
  country,
  last: _last = 3,
  limit = 15,
  statementType,
  interim,
  reportDates,
  disabled,
  vertical,
  indicatorDataSource,
  value,
  onChange,
}: ReportDatesRangeProps) {
  const {
    loading: isFinancialsAvailabilityLoading,
    allReportDates,
    reportsDatesByStatementType,
  } = useFinancialsAvailability(tickerSymbol, country, vertical, indicatorDataSource);

  if (isFinancialsAvailabilityLoading || (loading && reportDates.length === 0)) {
    return (
      <div
        aria-hidden={true}
        className={classNames(
          "relative flex h-9 w-full max-w-64 animate-pulse items-center justify-center rounded-lg bg-slate-200/60",
          "dark:bg-slate-900 dark:text-slate-300",
          "cursor-not-allowed select-none",
        )}
      />
    );
  }

  if (reportDates.length === 0) {
    return null;
  }

  const last = adjustedLast(_last, statementType);
  const minYear = getYear(min(allReportDates));
  const maxYear = getYear(max(allReportDates));
  const adjustedLimit = Math.min(Math.max(limit, 1), 30);
  const range = [Math.max(maxYear - last, minYear), maxYear];
  const initialRange = value && value[0] && value[1] ? [getYear(value[0]), getYear(value[1])] : range;

  return (
    <Value
      initial={{
        isMoving: false,
        lastTouchedThumb: 0,
        range: initialRange,
      }}
    >
      {({ value, set }) => {
        const isMovingOverBounds = value.isMoving && Math.abs(value.range[1] - value.range[0]) > adjustedLimit;
        const isMovingUnderBounds = value.isMoving && Math.abs(value.range[1] - value.range[0]) <= adjustedLimit;

        return (
          <div
            className={classNames(
              "relative w-full flex-col items-center justify-center space-y-1 rounded-lg p-0.5 lg:max-w-64 lg:max-w-sm xl:max-w-sm",
            )}
          >
            <div className="relative flex h-3.5">
              <Slider.Root
                step={1}
                disabled={loading || disabled}
                minStepsBetweenThumbs={1}
                min={minYear}
                max={maxYear}
                value={[...value.range]}
                className="group relative flex w-full touch-none select-none items-center data-[disabled]:cursor-not-allowed"
                onValueChange={(next) => {
                  set((current) => ({
                    ...current,
                    range: next,
                    isMoving: true,
                  }));
                }}
                onTouchStart={(event) => {
                  if (event.target instanceof HTMLElement) {
                    const thumb = parseFloat(event.target.getAttribute("data-thumb") || "");

                    set((current) => ({
                      ...current,
                      lastTouchedThumb: thumb,
                      isMoving: true,
                    }));
                  }
                }}
                onPointerDown={(event) => {
                  if (event.target instanceof HTMLElement) {
                    const thumb = parseFloat(event.target.getAttribute("data-thumb") || "");

                    set((current) => ({
                      ...current,
                      lastTouchedThumb: thumb,
                      isMoving: false,
                    }));
                  }
                }}
                onValueCommit={(range) => {
                  if (range.length !== 2) {
                    return;
                  }

                  const isLeftThumb = value.lastTouchedThumb === 0;
                  const isRightThumb = value.lastTouchedThumb === 1;

                  let adjustedRange = range;
                  if (isLeftThumb) {
                    const [leftValue, rightValue] = range;
                    const delta = rightValue - leftValue;
                    if (delta > adjustedLimit) {
                      adjustedRange = [leftValue, Math.min(leftValue + adjustedLimit, maxYear)];
                    }
                  }
                  if (isRightThumb) {
                    const [leftValue, rightValue] = range;
                    const delta = rightValue - leftValue;
                    if (delta > adjustedLimit) {
                      adjustedRange = [Math.max(rightValue - adjustedLimit, minYear), rightValue];
                    }
                  }

                  invariant(adjustedRange.length === 2, "Invalid range length");
                  invariant(
                    adjustedRange[0] < adjustedRange[1],
                    "Invalid range. Left value must be smaller than right",
                  );

                  const startYear = adjustedRange[0];
                  const endYear = adjustedRange[1];

                  const filterByDates = [...reportsDatesByStatementType[statementType]].filter((reportDate) => {
                    const year = getYear(reportDate);
                    return year >= startYear && year <= endYear;
                  });

                  set({
                    ...value,
                    range: adjustedRange,
                    isMoving: false,
                  });

                  if (filterByDates.length === 0) {
                    return;
                  }

                  onChange?.([min(filterByDates), max(filterByDates)]);
                }}
              >
                <Slider.Track
                  className={classNames(
                    "relative h-1 w-full grow overflow-hidden rounded-full bg-slate-200",
                    "dark:bg-slate-700",
                  )}
                >
                  <Slider.Range
                    className={classNames(
                      "absolute h-full bg-blue-600",
                      "group-data-[disabled]:bg-slate-300",
                      "group-data-[disabled]:dark:bg-slate-500",
                      "dark:bg-slate-500",
                    )}
                  />
                </Slider.Track>

                {[minYear, maxYear].map((year, index) => (
                  <Slider.Thumb
                    key={year}
                    data-thumb={index}
                    className={classNames(
                      "cursor-pointer",
                      "box-border block h-3 w-3 rounded-full border-2 border-blue-600 bg-white ring-offset-white",
                      "group-data-[disabled]:border-slate-300",
                      "group-data-[disabled]:border-slate-600 dark:border-slate-700 dark:bg-slate-400",
                      "group-data-[disabled]:dark:border-transparent group-data-[disabled]:dark:bg-slate-500",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300/30 focus-visible:ring-offset-0",
                      "disabled:pointer-events-none disabled:opacity-50",
                    )}
                  />
                ))}
              </Slider.Root>
            </div>

            <div
              className={classNames(
                "m-0 flex select-none items-center justify-between space-x-2 text-[9px] font-bold tracking-wider text-slate-500",
                "dark:text-slate-300",
                "whitespace-nowrap",
              )}
            >
              <span
                className={classNames({
                  "animate-pulse text-slate-800 dark:text-teal-400": value.isMoving && value.lastTouchedThumb === 0,
                })}
              >
                {lightFormat(min(reportDates), "MM/yyyy")}
              </span>

              {/* Separator */}
              <div
                data-orientation="horizontal"
                role="none"
                className="h-[1px] flex-1 bg-slate-200 dark:bg-slate-700"
              />

              <span
                className={classNames(
                  "rounded-full bg-slate-100 p-0.5 px-1.5 text-slate-600",
                  "dark:bg-slate-700 dark:text-slate-200",
                  {
                    "bg-rose-50 text-rose-600": isMovingOverBounds,
                    "bg-indigo-50 text-indigo-600": isMovingUnderBounds,
                  },
                )}
              >
                {loading ? (
                  <FormattedMessage defaultMessage="Carregando..." id="EMLr/e" />
                ) : (
                  <>
                    {isMovingOverBounds ? (
                      <FormattedMessage
                        defaultMessage="até {count, plural, one {# balanço} other {# balanços}}"
                        id="SPsbMd"
                        values={{
                          count: adjustedMovingRangeInYears(
                            Math.max(1, Math.min(adjustedLimit, value.range[1] - value.range[0])),
                            statementType,
                            interim || null,
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="{count, plural, one {# balanço} other {# balanços}}"
                        id="RBLo/+"
                        values={{
                          count: value.isMoving
                            ? adjustedMovingRangeInYears(
                                1 + Math.max(1, Math.min(adjustedLimit, value.range[1] - value.range[0])),
                                statementType,
                                interim || null,
                              )
                            : reportDates.length,
                        }}
                      />
                    )}
                  </>
                )}
              </span>

              {/* Separator */}
              <div
                data-orientation="horizontal"
                role="none"
                className="h-[1px] flex-1 bg-slate-200 dark:bg-slate-700"
              />

              <span
                className={classNames({
                  "animate-pulse text-slate-800 dark:text-teal-400": value.isMoving && value.lastTouchedThumb === 1,
                })}
              >
                {lightFormat(max(reportDates), "MM/yyyy")}
              </span>
            </div>
          </div>
        );
      }}
    </Value>
  );
}
